/**
 * ZAC's reasoning, as the panel shows it (docs/plans/document-to-plan-pipeline.md, R4): what is under way, and the
 * latest short lines, read while the panel is open. Informative only: a read that fails leaves what was shown.
 */
import { create } from 'zustand';
import { client } from '@/lib/api';
import type { plans } from '@/lib/client';

/** How often the open panel reads again. */
export const TRACE_POLL_MS = 2500;
/** The most lines the panel keeps. */
const MAX_LINES = 200;
const OPEN_KEY = 'zac.reasoning.open';

/** The document or plan whose pipeline the panel draws. */
export interface TraceSelection {
  kind: plans.TraceKind;
  id: string;
  title: string;
}

interface TraceState {
  open: boolean;
  /** Chosen in the panel; none: the first item under way is drawn. */
  selected: TraceSelection | null;
  select(selection: TraceSelection | null): void;
  active: plans.TraceActive[];
  entries: plans.TraceEntry[];
  toggle(): void;
  close(): void;
  read(): Promise<void>;
}

const wasOpen = (): boolean => {
  try {
    return localStorage.getItem(OPEN_KEY) === '1';
  } catch {
    return false;
  }
};

const remember = (open: boolean): void => {
  try {
    localStorage.setItem(OPEN_KEY, open ? '1' : '0');
  } catch {
    // Private mode: the panel just starts closed.
  }
};

export const useTraceStore = create<TraceState>((set, get) => ({
  open: wasOpen(),
  selected: null,
  select(selection) {
    set({ selected: selection });
  },
  active: [],
  entries: [],

  toggle() {
    const open = !get().open;
    remember(open);
    set({ open });
  },

  close() {
    remember(false);
    set({ open: false });
  },

  async read() {
    try {
      const after = get().entries[0]?.id ?? 0;
      const { active, entries } = await client.plans.getTrace({ after });
      // Two reads in flight can bring the same lines: each is kept once.
      set((s) => {
        const have = new Set(s.entries.map((e) => e.id));
        return { active, entries: [...entries.filter((e) => !have.has(e.id)), ...s.entries].slice(0, MAX_LINES) };
      });
    } catch {
      // The panel is informative: it keeps what it showed.
    }
  },
}));
